import React, { useMemo } from 'react'

import { Skeleton } from 'antd'
import { HStack, VStack } from 'govwell-ui'
import isNil from 'lodash.isnil'

import DashboardLabel from 'src/components/Visualizations/DashboardLabel'

import { formatNumber, FormatNumberArgs, NumberFormat } from '../../utils/number'
import Text, { TextSize } from '../Typography/Text'

export enum TileType {
  Currency = 'Currency',
  Days = 'Days',
  Number = 'Number',
  Percent = 'Percent',
  String = 'String',
}

type BaseTileProps = {
  isLoading?: boolean
  label: React.ReactNode
  tooltip?: string
  type: TileType
}

type NumberTileProps = BaseTileProps & {
  formatNumberArgs?: FormatNumberArgs
  type: TileType.Currency | TileType.Days | TileType.Number | TileType.Percent
  value?: number
}

type StringTileProps = BaseTileProps & {
  type: TileType.String
  value?: string
}

export type TileProps = NumberTileProps | StringTileProps

const Tile = (props: TileProps) => {
  const { type, value, label, tooltip, isLoading } = props

  const formattedValue = React.useMemo(() => {
    if (isNil(value)) {
      return ''
    }
    switch (type) {
      case TileType.String:
        return value
      case TileType.Currency:
        return formatNumber(value, { ...props.formatNumberArgs, format: NumberFormat.Currency })
      case TileType.Days:
      case TileType.Number:
      case TileType.Percent:
      default:
        return formatNumber(value, { ...props.formatNumberArgs, format: NumberFormat.Integer })
    }
  }, [props, type, value])

  const suffix = useMemo(() => {
    switch (type) {
      case TileType.Days:
        return value === 1 ? 'Day' : 'Days'
      case TileType.Percent:
        return '%'
      default:
        return ''
    }
  }, [type, value])

  return (
    <VStack
      gap={2}
      height="100%"
      width="100%"
      className="rounded-md border border-solid border-gray-300 p-4"
    >
      <DashboardLabel label={label} tooltip={tooltip} />
      {isLoading ? (
        <Skeleton.Input active />
      ) : (
        <HStack gap={1} align="baseline">
          <Text size={TextSize.ExtraLarge} strong>
            {formattedValue}
          </Text>
          {suffix && (
            <Text size={TextSize.Large} colorToken="colorTextSecondary">
              {suffix}
            </Text>
          )}
        </HStack>
      )}
    </VStack>
  )
}

export default React.memo(Tile)
